import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import {
  Box,
  Card,
  CardContent,
  Typography,
  LinearProgress,
  Avatar,
  Button,
  Chip,
  Grid,
  Divider,
  IconButton,
  Tooltip,
  Alert,
  Paper,
  CircularProgress,
  Collapse,
} from "@mui/material";
import {
  collection,
  query,
  where,
  onSnapshot,
  or,
  doc,
  getDoc,
} from "firebase/firestore";
import { useNavigate } from "react-router-dom";
import { AnimatedHint, AnimatedQuestion, AnimatedResult } from "../Animations";
import { db } from "../firebase";
import TimelineIcon from "@mui/icons-material/Timeline";
import { OptionButton } from "../../StyledComponents";
import CodeOverlay from "../CodeOverlay";
import { MathJaxContext, MathJax } from "better-react-mathjax";
import AccessTimeIcon from "@mui/icons-material/AccessTime";

import KeyIcon from "@mui/icons-material/Key";
import RuleIcon from "@mui/icons-material/Rule";

import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import LooksOneIcon from "@mui/icons-material/LooksOne";
import WifiTetheringIcon from "@mui/icons-material/WifiTethering";
import LooksTwoIcon from "@mui/icons-material/LooksTwo";
import Looks3Icon from "@mui/icons-material/Looks3";
import Looks4Icon from "@mui/icons-material/Looks4";
import TimerOutlinedIcon from "@mui/icons-material/TimerOutlined";

import CancelIcon from "@mui/icons-material/Cancel";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import HelpOutlineIcon from "@mui/icons-material/HelpOutline";
import { alpha } from "@mui/material/styles";
import Logger from "../Logger";

const QuizMonitor = ({ theme, styles, user }) => {
  const { quizId } = useParams();
  const [quizData, setQuizData] = useState(null);
  const [studentResults, setStudentResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const [showCodeOverlay, setShowCodeOverlay] = useState(null);

  const [expandedStudents, setExpandedStudents] = useState({});
  const [showQuestions, setShowQuestions] = useState({});

  useEffect(() => {
    Logger.log("studentResults: ", JSON.stringify(studentResults, null, 2));

    Logger.log("quizData: ", JSON.stringify(quizData, null, 2));
  }, [studentResults, quizData]);

  useEffect(() => {
    // Fetch quiz details
    const fetchQuizData = async () => {
      const quizDoc = await getDoc(doc(db, "quiz", quizId));
      if (quizDoc.exists()) {
        setQuizData(quizDoc.data());
      }
    };
    fetchQuizData();

    // Set up real-time listener for student results

    const q = query(
      collection(db, "studentResults"),
      or(where("quizId", "==", quizId), where("originalQuizId", "==", quizId)),
    );
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const results = [];
      snapshot.forEach((doc) => {
        results.push(doc.data());
      });
      setStudentResults(results);
      setLoading(false);
    });

    // const q = query(
    //   collection(db, "studentResults"),
    //   where("quizId", "==", quizId),
    // );

    // const unsubscribe = onSnapshot(q, (snapshot) => {
    //   const results = [];
    //   snapshot.forEach((doc) => {
    //     results.push(doc.data());
    //   });
    //   setStudentResults(results);

    //   setLoading(false);
    // });

    return () => unsubscribe();
  }, [quizId]);

  useEffect(() => {
    studentResults.forEach((result) => {
      if (result.detailedResults) {
        result.detailedResults.forEach((_, questionIndex) => {
          setShowQuestions((prev) => ({
            ...prev,
            [`${result.studentName}-${questionIndex}`]: true,
          }));
        });
      }
    });
  }, [studentResults]);

  const formatTime = (seconds) => {
    if (!seconds) return null;
    if (seconds < 60) return `${seconds} sekunder`;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    return `${minutes} min${minutes > 1 ? "" : ""} ${remainingSeconds} sek`;
  };
  const formatTotalTime = (seconds) => {
    if (!seconds) return null;
    const minutes = Math.floor(seconds / 60);
    const remainingSeconds = seconds % 60;
    if (minutes === 0) return `${remainingSeconds} sekunder`;
    return `${minutes} min${minutes > 1 ? "" : ""} ${remainingSeconds} sek`;
  };

  const toggleStudentDetails = (studentName) => {
    setExpandedStudents((prev) => ({
      ...prev,
      [studentName]: !prev[studentName],
    }));
  };

  if (loading) {
    return (
      <Box sx={{ p: 4, textAlign: "center" }}>
        <CircularProgress />
      </Box>
    );
  }

  if (!studentResults.length) {
    return (
      <>
        <Box sx={{ textAlign: "center" }}>
          <WifiTetheringIcon
            sx={{ mb: 1, fontSize: "4rem", color: "text.secondary" }}
          />
          <Typography
            variant="h4"
            gutterBottom
            sx={{
              textAlign: "center",
              mb: 2,
              fontWeight: "600",
              fontSize: "1.4rem",
            }}
          >
            Invänta eleverna...
          </Typography>
        </Box>

        <Box
          sx={{
            ...styles.freshBox,
            maxWidth: 500,
            mx: "auto",

            p: 5,
            textAlign: "center",
          }}
        >
          <Typography sx={{ mb: 3, fontSize: "1.2rem" }}>
            Resultaten visas här i realtid när eleverna börjar svara.
            <br />
            <br />
            Antingen ger du dem provnyckeln nedan (inget konto krävs), eller
            klicka på dela med elever på "Mina quiz" (konto krävs):
          </Typography>

          <Tooltip title="Förstora provnyckeln" placement="top">
            <Chip
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setShowCodeOverlay(quizId.substring(0, 6));
              }}
              label={
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    pointerEvents: "auto !important",
                  }}
                >
                  <KeyIcon sx={{ fontSize: 25, mr: 1 }} />
                  {quizId.substring(0, 6)}
                </Box>
              }
              size="small"
              sx={{
                ...styles.greenChip,
                height: "50px",
                cursor: "pointer",
                fontSize: "1.2rem",
                paddingLeft: "10px",
                paddingRight: "10px",
                pointerEvents: "auto !important",
                "&:hover": {
                  backgroundColor: `${theme.palette.primary.main} !important`,
                  color: "white !important",
                },
              }}
            />
          </Tooltip>
          {showCodeOverlay && (
            <CodeOverlay
              code={showCodeOverlay}
              quizId={quizId}
              onClose={() => setShowCodeOverlay(null)}
            />
          )}
        </Box>
      </>
    );
  }

  return (
    <>
      <MathJaxContext
        config={{
          tex: {
            inlineMath: [
              ["$", "$"],
              ["\\(", "\\)"],
            ],
            displayMath: [
              ["$$", "$$"],
              ["\\[", "\\]"],
            ],
          },
          chtml: {
            scale: 1.3,
            fontFamily: "Arial",
            matchFontHeight: false,
            displayAlign: "center",
            displayIndent: "0",
          },
        }}
      >
        <Box sx={{ ...styles.container, textAlign: "left" }}>
          <Box
            sx={{
              ...styles.innerContainer,
            }}
          >
            <Box sx={{ ...styles.headlineLessonBox, mt: 4 }}>
              <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                Övervaka / Resultat för: {quizData?.quizName}
              </Typography>
            </Box>
            <Box
              sx={{
                textAlign: "center",
                mb: 2,
                position: "absolute",
                top: 15,
                right: 27,
              }}
            >
              <Tooltip title="Förstora provnyckeln" placement="top">
                <Chip
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    setShowCodeOverlay(quizId.substring(0, 6));
                  }}
                  label={
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        pointerEvents: "auto !important",
                      }}
                    >
                      <KeyIcon sx={{ fontSize: 20, mr: 1 }} />
                      {quizId.substring(0, 6)}
                    </Box>
                  }
                  size="small"
                  sx={{
                    ...styles.greenChip,
                    height: "30px",
                    cursor: "pointer",
                    fontSize: "1rem",
                    paddingLeft: "10px",
                    paddingRight: "10px",
                    pointerEvents: "auto !important",
                    "&:hover": {
                      backgroundColor: `${theme.palette.primary.main} !important`,
                      color: "white !important",
                    },
                  }}
                />
              </Tooltip>
            </Box>
            {showCodeOverlay && (
              <CodeOverlay
                code={showCodeOverlay}
                quizId={quizId}
                onClose={() => setShowCodeOverlay(null)}
              />
            )}

            <Grid container spacing={3}>
              {/* Summary Card */}
              <Grid item xs={12}>
                <Box sx={{ ...styles.freshBox, ...styles.softBoxShadow }}>
                  <Grid container spacing={2}>
                    <Grid item xs={12} md={3}>
                      <Paper
                        sx={{
                          ...styles.freshBox,
                          ...styles.softBoxShadow,
                          p: 2,
                          bgcolor: alpha(theme.palette.primary.main, 0.1),
                        }}
                      >
                        <Typography variant="subtitle2" color="textSecondary">
                          Aktiva elever
                        </Typography>
                        <Typography variant="h4">
                          {studentResults.length}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper
                        sx={{
                          ...styles.freshBox,
                          ...styles.softBoxShadow,
                          p: 2,
                          bgcolor: alpha(theme.palette.success.main, 0.1),
                        }}
                      >
                        <Typography variant="subtitle2" color="textSecondary">
                          Genomsnittligt resultat
                        </Typography>
                        <Typography variant="h4">
                          {studentResults.some((r) => r.score !== undefined)
                            ? `${Math.round(
                                studentResults.reduce(
                                  (acc, curr) => acc + (curr.score || 0),
                                  0,
                                ) /
                                  studentResults.filter(
                                    (r) => r.score !== undefined,
                                  ).length,
                              )}%`
                            : "0%"}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper
                        sx={{
                          ...styles.freshBox,
                          ...styles.softBoxShadow,
                          p: 2,
                          bgcolor: alpha(theme.palette.info.main, 0.1),
                        }}
                      >
                        <Typography variant="subtitle2" color="textSecondary">
                          Genomsnittlig tid
                        </Typography>
                        <Typography variant="h4">
                          {formatTotalTime(
                            Math.round(
                              studentResults.reduce(
                                (acc, curr) => acc + curr.totalTimeSpent,
                                0,
                              ) / studentResults.length,
                            ),
                          )}
                        </Typography>
                      </Paper>
                    </Grid>
                    <Grid item xs={12} md={3}>
                      <Paper
                        sx={{
                          ...styles.freshBox,
                          ...styles.softBoxShadow,
                          p: 2,
                          bgcolor: alpha(theme.palette.warning.main, 0.1),
                        }}
                      >
                        <Typography variant="subtitle2" color="textSecondary">
                          Färdiga elever
                        </Typography>
                        <Typography variant="h4">
                          {studentResults.filter((r) => r.isComplete).length}
                        </Typography>
                      </Paper>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <Box sx={{ ...styles.headlineLessonBox, mt: 4 }}>
                  <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                    Tabellöversikt
                  </Typography>
                </Box>

                <Box
                  sx={{
                    ...styles.freshBox,
                    ...styles.softBoxShadow,
                    paddingBottom: 4,
                  }}
                >
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ fontSize: "1rem" }}>Elev</TableCell>
                        {studentResults.some((r) => r.detailedResults?.length)
                          ? Array.from({
                              length: studentResults.find(
                                (r) => r.detailedResults?.length,
                              )?.detailedResults.length,
                            }).map((_, index) => (
                              <TableCell
                                sx={{ fontSize: "1rem" }}
                                key={index}
                                align="center"
                              >
                                Fråga {index + 1}
                              </TableCell>
                            ))
                          : null}
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {studentResults.length === 0 ? (
                        <TableRow>
                          <TableCell
                            colSpan={quizData?.questions?.length + 1}
                            align="center"
                            sx={{ fontStyle: "italic" }}
                          >
                            Inga elever har anslutit än
                          </TableCell>
                        </TableRow>
                      ) : (
                        studentResults.map((result) => (
                          <TableRow key={result.studentName}>
                            <TableCell sx={{ fontSize: "1rem" }}>
                              {result.studentName}
                            </TableCell>
                            {!result.detailedResults ||
                            result.detailedResults.length === 0 ? (
                              <TableCell
                                colSpan={300}
                                align="center"
                                sx={{ fontStyle: "italic" }}
                              >
                                Ansluten men har inte börjat än
                              </TableCell>
                            ) : (
                              result.detailedResults.map((question, index) => (
                                <TableCell
                                  key={index}
                                  sx={{
                                    fontSize: "1rem",
                                    bgcolor: !question.selectedOption
                                      ? alpha(theme.palette.grey[500], 0.1)
                                      : question.isCorrect
                                        ? alpha(theme.palette.success.main, 0.1)
                                        : alpha(theme.palette.error.main, 0.1),
                                  }}
                                  align="center"
                                >
                                  <AnimatedResult
                                    show={!!question.selectedOption}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        justifyContent: "center",
                                        gap: 1,
                                      }}
                                    >
                                      {question.selectedOption &&
                                        (question.isCorrect ? (
                                          <CheckCircleIcon
                                            color="success"
                                            sx={{ fontSize: 16 }}
                                          />
                                        ) : (
                                          <CancelIcon
                                            color="error"
                                            sx={{ fontSize: 18 }}
                                          />
                                        ))}
                                    </Box>
                                  </AnimatedResult>
                                </TableCell>
                              ))
                            )}
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </Box>
              </Grid>
              {/* Individual Student Results */}

              <Grid item xs={12}>
                <Box sx={{ ...styles.headlineLessonBox, mt: 5, mb: -1 }}>
                  <Typography variant="h6" sx={styles.headlineLessonBoxTitle}>
                    Detaljerade resultat
                  </Typography>
                </Box>
              </Grid>
              {studentResults.map((result) => (
                <Grid item xs={12} key={result.studentName}>
                  <Box
                    onClick={() => toggleStudentDetails(result.studentName)}
                    sx={{
                      ...styles.freshBox,
                      ...styles.softBoxShadow,
                      cursor: "pointer",
                      background: expandedStudents[result.studentName]
                        ? "transparent"
                        : "undefined",
                      transition: "all 0.3s ease",
                      boxShadow: expandedStudents[result.studentName]
                        ? "transparent"
                        : "undefined",
                      borderColor: expandedStudents[result.studentName]
                        ? "transparent"
                        : "undefined",
                      "&:hover": {
                        transform: "translateY(-4px)",
                        background: expandedStudents[result.studentName]
                          ? "transparent"
                          : "undefined",
                        borderColor: expandedStudents[result.studentName]
                          ? "transparent"
                          : "primary.main",
                      },
                    }}
                  >
                    <Box sx={{ display: "flex", alignItems: "center", mb: 0 }}>
                      <Typography variant="h6" sx={{ fontWeight: "600" }}>
                        {result.studentName}
                      </Typography>
                      {result.isComplete && (
                        <Chip
                          label="Klar"
                          color="success"
                          size="small"
                          sx={{ ml: 2 }}
                        />
                      )}
                      <Box
                        sx={{
                          ml: "auto",
                          display: "flex",
                          alignItems: "center",
                          gap: 2,
                        }}
                      >
                        <Tooltip title="Total tid">
                          <Box
                            sx={{
                              display: "flex",
                              alignItems: "center",
                              gap: 0.5,
                            }}
                          >
                            <AccessTimeIcon
                              color="action"
                              sx={{ fontSize: "1rem" }}
                            />
                            <Typography variant="body2">
                              {formatTime(result.totalTimeSpent)}
                            </Typography>
                          </Box>
                        </Tooltip>
                        <Box
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 0.5,
                          }}
                        >
                          <RuleIcon color="action" sx={{ fontSize: "1rem" }} />
                          <Typography variant="body2">
                            {`${result.detailedResults?.filter((q) => q.isCorrect).length || 0}/${result.detailedResults?.length || 0} rätt!`}
                          </Typography>
                        </Box>

                        {expandedStudents[result.studentName] ? (
                          <ExpandLessIcon color="action" />
                        ) : (
                          <ExpandMoreIcon color="action" />
                        )}
                      </Box>
                    </Box>
                    <Collapse in={expandedStudents[result.studentName]}>
                      <Divider sx={{ my: 2 }} />

                      {/* Question Progress */}
                      {/* Question Progress */}
                      {/* Question Progress */}
                      <Grid container spacing={2}>
                        {result.detailedResults &&
                        result.detailedResults.length > 0 ? (
                          result.detailedResults.map((question, index) => {
                            // Get the matching quiz question data that contains the options
                            const quizQuestion =
                              quizData?.results?.[index] || {};

                            return (
                              <Grid item xs={12} key={index}>
                                <AnimatedQuestion
                                  show={
                                    showQuestions[
                                      `${result.studentName}-${index}`
                                    ]
                                  }
                                >
                                  <Box
                                    sx={{
                                      marginBottom: 4,
                                      padding: 5,
                                      paddingBottom: 0,
                                      backgroundColor:
                                        theme.palette.background.paper,
                                      borderRadius: "8px",
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        display: "flex",
                                        alignItems: "center",
                                        width: "100%",
                                        mb: 2,
                                      }}
                                    >
                                      <Typography
                                        variant="h6"
                                        sx={{
                                          marginBottom: 0,
                                          fontSize: "0.9rem",
                                          color: "text.secondary",
                                        }}
                                      >
                                        Fråga {index + 1}:
                                      </Typography>
                                      <Box sx={{ flexGrow: 1 }} />
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 0.5,
                                          color: theme.palette.text.secondary,
                                          fontSize: "0.9rem",
                                        }}
                                      >
                                        <TimerOutlinedIcon
                                          sx={{ fontSize: "1rem" }}
                                        />
                                        {formatTime(question.timeSpent)}
                                      </Box>
                                    </Box>

                                    <Typography
                                      variant="body1"
                                      sx={{
                                        marginBottom: 2,
                                        fontWeight: "500",
                                        fontSize: "1.2rem",
                                      }}
                                    >
                                      <MathJax>{question.question}</MathJax>
                                    </Typography>
                                    {!question.selectedOption && (
                                      <Box
                                        sx={{
                                          paddingBottom: "30px !important",
                                        }}
                                      >
                                        <Typography
                                          sx={{
                                            fontStyle: "italic",
                                            color: "text.secondary",
                                            mt: 2,

                                            mb: 2,
                                          }}
                                        >
                                          Användaren har ännu inte svarat på
                                          denna fråga.
                                        </Typography>
                                      </Box>
                                    )}
                                    {question.selectedOption && (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          mt: 0,
                                          mb: 4,
                                          flexDirection: {
                                            xs: "column",
                                            sm: "row",
                                          },
                                          flexWrap: "wrap",
                                          justifyContent: "space-between",
                                          gap: 2,
                                        }}
                                      >
                                        {quizData?.results &&
                                        quizData.results[index] &&
                                        quizData.results[index].type ===
                                          "exam" ? (
                                          <Box sx={{ mt: 4, mb: 4 }}>
                                            <Typography
                                              variant="h6"
                                              sx={{
                                                mb: 1,
                                                fontSize: "1.1rem",
                                                color: "text.secondary",
                                              }}
                                            >
                                              Elevens svar:
                                            </Typography>
                                            <Typography
                                              sx={{
                                                mb: 3,
                                                whiteSpace: "pre-wrap",
                                              }}
                                            >
                                              <MathJax>
                                                {question.selectedOption}
                                              </MathJax>
                                            </Typography>

                                            <Box
                                              sx={{
                                                p: 2,
                                                mb: 3,
                                                borderRadius: 1,
                                                backgroundColor:
                                                  question.isCorrect
                                                    ? theme.palette.mode ===
                                                      "dark"
                                                      ? "rgba(52, 168, 83, 0.15)"
                                                      : "#e6f4ea"
                                                    : theme.palette.mode ===
                                                        "dark"
                                                      ? "rgba(234, 67, 53, 0.15)"
                                                      : "#fce8e6",
                                                borderLeft: `4px solid ${
                                                  question.isCorrect
                                                    ? "#34a853"
                                                    : "#ea4335"
                                                }`,
                                              }}
                                            >
                                              <Typography
                                                variant="h6"
                                                sx={{
                                                  mb: 1,
                                                  fontSize: "1rem",
                                                  color: question.isCorrect
                                                    ? "#34a853"
                                                    : "#ea4335",
                                                }}
                                              >
                                                {question.isCorrect
                                                  ? "Korrekt svar!"
                                                  : "Fel svar"}
                                              </Typography>
                                              <Typography
                                                sx={{ whiteSpace: "pre-wrap" }}
                                              >
                                                {question.isCorrect
                                                  ? "Förklaring varför det var korrekt:"
                                                  : "Förklaring varför det var fel:"}
                                              </Typography>
                                              <Typography
                                                sx={{
                                                  mt: 1,
                                                  whiteSpace: "pre-wrap",
                                                }}
                                              >
                                                {question.explanation}
                                              </Typography>
                                            </Box>

                                            <Typography
                                              variant="h6"
                                              sx={{
                                                mb: 1,
                                                fontSize: "1.1rem",
                                                color: "text.secondary",
                                              }}
                                            >
                                              Korrekt svar:
                                            </Typography>
                                            <Typography
                                              sx={{ whiteSpace: "pre-wrap" }}
                                            >
                                              <MathJax>
                                                {question.correctOption}
                                              </MathJax>
                                            </Typography>
                                          </Box>
                                        ) : (
                                          <Box
                                            sx={{
                                              display: "flex",
                                              mt: 2,
                                              mb: 4,
                                              flexDirection: {
                                                xs: "column",
                                                sm: "row",
                                              },
                                              flexWrap: "wrap",
                                              justifyContent: "space-between",
                                              gap: 2,
                                              width: "100%",
                                            }}
                                          >
                                            {quizQuestion.options?.map(
                                              (option, optIndex) => {
                                                const isSelected =
                                                  option ===
                                                  question.selectedOption;
                                                const isCorrect =
                                                  option ===
                                                  question.correctOption;

                                                return (
                                                  <OptionButton
                                                    key={optIndex}
                                                    variant="outlined"
                                                    sx={{
                                                      backgroundColor:
                                                        isSelected && isCorrect
                                                          ? theme.palette
                                                              .mode === "dark"
                                                            ? "rgba(52, 168, 83, 0.15)!important"
                                                            : "#e6f4ea!important"
                                                          : isSelected &&
                                                              !isCorrect
                                                            ? theme.palette
                                                                .mode === "dark"
                                                              ? "rgba(234, 67, 53, 0.15)!important"
                                                              : "#fce8e6!important"
                                                            : isCorrect
                                                              ? theme.palette
                                                                  .mode ===
                                                                "dark"
                                                                ? "rgba(52, 168, 83, 0.15)!important"
                                                                : "#e6f4ea!important"
                                                              : "theme.palette.divider",
                                                      borderColor:
                                                        isSelected && isCorrect
                                                          ? "#34a853!important"
                                                          : isSelected &&
                                                              !isCorrect
                                                            ? "#ea4335!important"
                                                            : isCorrect
                                                              ? "#34a853!important"
                                                              : "theme.palette.divider",
                                                      color:
                                                        theme.palette.text
                                                          .primary,
                                                      opacity: "1!important",
                                                      pointerEvents: "none",
                                                    }}
                                                  >
                                                    {optIndex === 0 && (
                                                      <LooksOneIcon
                                                        sx={{
                                                          marginRight: "8px",
                                                          color: `${
                                                            option ===
                                                            question.correctOption
                                                              ? "rgb(17 161 70)!important"
                                                              : option ===
                                                                  question.selectedOption
                                                                ? "rgb(178 28 54) !important"
                                                                : "#8b5cf6!important"
                                                          }`,
                                                        }}
                                                      />
                                                    )}
                                                    {optIndex === 1 && (
                                                      <LooksTwoIcon
                                                        sx={{
                                                          marginRight: "8px",
                                                          color: `${
                                                            option ===
                                                            question.correctOption
                                                              ? "rgb(17 161 70) !important"
                                                              : option ===
                                                                  question.selectedOption
                                                                ? "rgb(178 28 54) !important"
                                                                : "#8b5cf6!important"
                                                          }`,
                                                        }}
                                                      />
                                                    )}
                                                    {optIndex === 2 && (
                                                      <Looks3Icon
                                                        sx={{
                                                          marginRight: "8px",
                                                          color: `${
                                                            option ===
                                                            question.correctOption
                                                              ? "rgb(17 161 70) !important"
                                                              : option ===
                                                                  question.selectedOption
                                                                ? "rgb(178 28 54) !important"
                                                                : "#8b5cf6!important"
                                                          }`,
                                                        }}
                                                      />
                                                    )}
                                                    {optIndex === 3 && (
                                                      <Looks4Icon
                                                        sx={{
                                                          marginRight: "8px",
                                                          color: `${
                                                            option ===
                                                            question.correctOption
                                                              ? "rgb(17 161 70) !important"
                                                              : option ===
                                                                  question.selectedOption
                                                                ? "rgb(178 28 54) !important"
                                                                : "#8b5cf6!important"
                                                          }`,
                                                        }}
                                                      />
                                                    )}
                                                    {option}
                                                  </OptionButton>
                                                );
                                              },
                                            )}

                                            {question.revealedHints &&
                                              question.revealedHints.length >
                                                0 && (
                                                <Box
                                                  sx={{
                                                    mt: 5,
                                                    mb: 1,
                                                    width: "100%",
                                                  }}
                                                >
                                                  <Typography
                                                    variant="h6"
                                                    sx={{
                                                      fontSize: "1rem",
                                                      color: "text.secondary",
                                                      mb: 2,
                                                    }}
                                                  >
                                                    Använda ledtrådar:
                                                  </Typography>
                                                  {question.revealedHints.map(
                                                    (hintIndex) => (
                                                      <Box
                                                        key={hintIndex}
                                                        sx={{
                                                          p: 2,
                                                          mb: 2,
                                                          bgcolor: alpha(
                                                            theme.palette
                                                              .primary.main,
                                                            theme.palette
                                                              .mode === "dark"
                                                              ? 0.15 +
                                                                  hintIndex *
                                                                    0.1
                                                              : 0.08 +
                                                                  hintIndex *
                                                                    0.05,
                                                          ),
                                                          borderRadius: "8px",
                                                          border: `1px solid ${theme.palette.primary.main}40`,
                                                          display: "flex",
                                                          alignItems:
                                                            "flex-start",
                                                          gap: 1.5,
                                                        }}
                                                      >
                                                        <CheckCircleIcon
                                                          sx={{
                                                            color:
                                                              theme.palette
                                                                .primary.main,
                                                            mt: "2px",
                                                          }}
                                                        />
                                                        <Typography
                                                          sx={{
                                                            color:
                                                              theme.palette
                                                                .mode === "dark"
                                                                ? "text.white"
                                                                : "text.primary",
                                                            flex: 1,
                                                          }}
                                                        >
                                                          <Box
                                                            component="span"
                                                            sx={{
                                                              fontWeight: 700,
                                                              mr: 1,
                                                            }}
                                                          >
                                                            Ledtråd{" "}
                                                            {hintIndex + 1}:
                                                          </Box>
                                                          {
                                                            quizQuestion.hints[
                                                              hintIndex
                                                            ]
                                                          }
                                                        </Typography>
                                                      </Box>
                                                    ),
                                                  )}
                                                </Box>
                                              )}
                                          </Box>
                                        )}
                                      </Box>
                                    )}
                                  </Box>
                                </AnimatedQuestion>
                              </Grid>
                            );
                          })
                        ) : (
                          <Grid item xs={12}>
                            <Typography sx={{ fontStyle: "italic" }}>
                              Ansluten men har inte börjat än
                            </Typography>
                          </Grid>
                        )}
                      </Grid>
                    </Collapse>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </Box>{" "}
        </Box>
      </MathJaxContext>
    </>
  );
};

export default QuizMonitor;
